import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibChevronRightIcon } from '@chewy/kib-icons-react'
import { KibTooltip } from '@chewy/kib-tooltip-react'
import { PermissionArea } from '@pbt/pbt-ui-components'

import FeatureToggle from '~/constants/featureToggle'
import { addSOAP } from '~/store/actions/soap'
import { useOpenInvoice } from '~/store/hooks/finance'
import { getCRUDByArea } from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'
import { getTimetableEvent } from '~/store/reducers/timetable'
import type { Conversation } from '~/types'

import useCommunicationsMenuDialogs from './useCommunicationsMenuDialogs'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import '@chewy/kib-tooltip-styles/dist/kib-tooltip.css'
import styles from './CommunicationsHeaderMenu.module.scss'

const MAIN_MENU_OFFSET = [-60, 10] as const
const SUBMENU_OFFSET = [125, 5] as const

interface CommunicationsHeaderMenuProps {
  conversation: Conversation
  triggerRef: React.RefObject<HTMLButtonElement>
}

const CommunicationsHeaderMenu = ({
  triggerRef,
  conversation,
}: CommunicationsHeaderMenuProps) => {
  const { t } = useTranslation(['Common', 'Menu', 'TimeTable'])
  const dispatch = useDispatch()

  const clientId = conversation?.client as string
  const patientId = conversation?.patient
  const appointmentId = conversation?.event?.id
  const eventBusinessId = conversation?.businessId
  const eventSoapId = conversation?.event?.soaps?.[0]?.id

  const appointment = useSelector(getTimetableEvent(appointmentId))

  const SOAPPermissions = useSelector(getCRUDByArea(PermissionArea.SOAP))
  const invoicePermissions = useSelector(getCRUDByArea(PermissionArea.INVOICE))
  const taskPermissions = useSelector(getCRUDByArea(PermissionArea.TASK))
  const appointmentPermissions = useSelector(
    getCRUDByArea(PermissionArea.EVENT_APPOINTMENT),
  )
  const communicationPermissions = useSelector(
    getCRUDByArea(PermissionArea.COMMUNICATION),
  )
  const patientPermissions = useSelector(getCRUDByArea(PermissionArea.PATIENT))

  const isCvcRolesEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CVC_ROLES),
  )
  const isPatientSharingEnabled = useSelector(
    getFeatureToggle(FeatureToggle.PATIENT_SHARING),
  )

  const isPatientUpdateDenied = isCvcRolesEnabled && !patientPermissions.update

  const {
    openAppointmentDialog,
    openEditAppointmentDialog,
    openAttachmentHistoryDialog,
    openNewConversationDialog,
    openDiagnosisHistoryDialog,
    openAddReminderDialog,
    openVaccineHistoryDialog,
    openVitalsDialog,
    openEstimatesListDialog,
    openPrintCageLabelDialog,
    openTaskDialog,
  } = useCommunicationsMenuDialogs()

  const addButtonRef = useRef<HTMLButtonElement>(null)

  const openInvoice = useOpenInvoice(clientId)

  const hasLinkedAppointment = Boolean(appointmentId)

  const handleAddAppointment = () => {
    openAppointmentDialog({
      clientId,
      patientId,
    })
  }

  const handleAddSOAP = () => {
    if (!appointmentId) return

    dispatch(
      addSOAP(appointmentId, isPatientSharingEnabled ? eventBusinessId : null),
    )
  }

  const handleOpenInvoice = () => {
    openInvoice({
      clientId,
      eventId: appointmentId,
      patientId,
      id: undefined,
    })
  }

  const addMenuItems = [
    {
      label: t('Menu:SOAP_TOP_MENU.APPOINTMENT_NEW'),
      onClick: handleAddAppointment,
      disabled: !appointmentPermissions.create,
    },
    {
      label: t('Common:ATTACHMENT'),
      onClick: () => openAttachmentHistoryDialog({ clientId, patientId }),
      disabled: !patientPermissions.update,
    },
    {
      label: t('Common:CONVERSATION'),
      onClick: () => openNewConversationDialog({ clientId, patientId }),
      disabled: !communicationPermissions.create,
    },
    {
      label: t('Common:DIAGNOSIS_HISTORY'),
      onClick: () => openDiagnosisHistoryDialog({ clientId, patientId }),
      disabled: isPatientUpdateDenied,
    },
    {
      label: t('Common:REMINDER'),
      onClick: () => openAddReminderDialog({ patientId, clientId }),
      disabled: !SOAPPermissions.update,
    },
    hasLinkedAppointment && {
      label: t('Menu:SOAP_TOP_MENU.SOAP_TO_CURRENT_APPOINTMENT'),
      onClick: handleAddSOAP,
      disabled: !SOAPPermissions.create,
    },
    {
      label: t('Common:TASK'),
      onClick: () =>
        openTaskDialog({
          clientId,
          patientId,
          appointmentId,
          dueDate: appointment?.scheduledEndDatetime,
        }),
      disabled: !taskPermissions.create,
    },
    {
      label: t('Common:VACCINE_HISTORY'),
      onClick: () => openVaccineHistoryDialog({ clientId, patientId }),
      disabled: isPatientUpdateDenied,
    },
    {
      label: t('Common:VITALS'),
      onClick: () => openVitalsDialog({ clientId, patientId }),
      disabled: isPatientUpdateDenied,
    },
  ].filter(Boolean) as Array<{
    disabled: boolean
    label: string
    onClick: () => void
  }>

  return (
    <KibTooltip
      className={styles.tooltip}
      placement="bottom"
      popperModifiers={[
        {
          name: 'offset',
          options: {
            offset: MAIN_MENU_OFFSET,
          },
        },
        {
          name: 'flip',
          enabled: false,
        },
      ]}
      trigger={triggerRef}
    >
      <div
        aria-label={t('Common:ACTIONS')}
        className={styles.tooltipContent}
        role="menu"
      >
        <div className={styles.menuContainer}>
          {patientId && (
            <>
              <KibButtonNew
                aria-expanded="false"
                aria-haspopup="menu"
                className={styles.menuButton}
                emphasis="tertiary"
                icon={<KibChevronRightIcon />}
                iconPosition="end"
                ref={addButtonRef}
                theme="utility"
              >
                {t('Common:ADD_TO_PATIENT_RECORD')}
              </KibButtonNew>
              <KibTooltip
                className={styles.tooltip}
                placement="left"
                popperModifiers={[
                  {
                    name: 'offset',
                    options: {
                      offset: SUBMENU_OFFSET,
                    },
                  },
                  {
                    name: 'flip',
                    enabled: false,
                  },
                ]}
                trigger={addButtonRef}
              >
                <ul
                  aria-label={t('Common:ADD_TO_PATIENT_RECORD')}
                  className={styles.menuContainer}
                  role="menu"
                >
                  {addMenuItems.map((button) => (
                    <li key={`${button.label}-add-menu-item`} role="none">
                      <KibButtonNew
                        className={styles.menuButton}
                        disabled={button.disabled}
                        emphasis="tertiary"
                        role="menuitem"
                        theme="utility"
                        onClick={button.onClick}
                      >
                        {button.label}
                      </KibButtonNew>
                    </li>
                  ))}
                </ul>
              </KibTooltip>
            </>
          )}

          {hasLinkedAppointment && (
            <KibButtonNew
              className={styles.menuButton}
              disabled={!appointmentPermissions.update}
              emphasis="tertiary"
              role="menuitem"
              theme="utility"
              onClick={() =>
                openEditAppointmentDialog({
                  appointmentId,
                  isAppointmentType: true,
                })
              }
            >
              {t(
                'TimeTable:ADD_APPOINTMENT_COMPONENT.EDIT_CURRENT_APPOINTMENT',
              )}
            </KibButtonNew>
          )}
          {eventSoapId && (
            <KibButtonNew
              className={styles.menuButton}
              disabled={!invoicePermissions.create}
              emphasis="tertiary"
              role="menuitem"
              theme="utility"
              onClick={() => openEstimatesListDialog({ patientId })}
            >
              {t('Common:GO_TO_ESTIMATE')}
            </KibButtonNew>
          )}

          <KibButtonNew
            className={styles.menuButton}
            disabled={!invoicePermissions.create}
            emphasis="tertiary"
            role="menuitem"
            theme="utility"
            onClick={handleOpenInvoice}
          >
            {t('Common:GO_TO_CHARGE_SHEET')}
          </KibButtonNew>

          {patientId && (
            <KibButtonNew
              className={styles.menuButton}
              disabled={!patientPermissions.read}
              emphasis="tertiary"
              role="menuitem"
              theme="utility"
              onClick={() => openPrintCageLabelDialog({ clientId, patientId })}
            >
              {t('Common:PRINT_CAGE_CARD_LABEL')}
            </KibButtonNew>
          )}
        </div>
      </div>
    </KibTooltip>
  )
}

export default CommunicationsHeaderMenu
